"use client";

import { useEffect, useState } from "react";

import { useAppConfig } from "@/lib/config";

export interface DisplayProfile {
	currency: string;
	symbol: string;
	locale: string;
	displayRate: number;
}

/**
 * Fetch the org's billing profile (display currency + USD→currency rate). The
 * credit ledger is USD; the playground renders it in the org's currency.
 */
export function useBillingProfile(
	orgId: string | undefined,
): DisplayProfile | null {
	const { apiUrl } = useAppConfig();
	const [profile, setProfile] = useState<DisplayProfile | null>(null);
	useEffect(() => {
		if (!orgId) {
			return;
		}
		let cancelled = false;
		fetch(`${apiUrl}/orgs/${orgId}/billing-profile`, { credentials: "include" })
			.then((r) => (r.ok ? r.json() : null))
			.then((d) => {
				if (!cancelled && d) {
					setProfile(d);
				}
			})
			.catch(() => {
				/* keep null → USD default */
			});
		return () => {
			cancelled = true;
		};
	}, [apiUrl, orgId]);
	return profile;
}

/** Format a USD ledger amount in the org's display currency. */
export function formatMoney(
	usdAmount: number | string | null | undefined,
	profile?: DisplayProfile | null,
): string {
	const usd = typeof usdAmount === "string" ? parseFloat(usdAmount) : usdAmount;
	const amount = Number.isFinite(usd as number) ? (usd as number) : 0;
	const value = amount * (profile?.displayRate ?? 1);
	try {
		return new Intl.NumberFormat(profile?.locale ?? "en-US", {
			style: "currency",
			currency: profile?.currency ?? "USD",
			maximumFractionDigits: 2,
		}).format(value);
	} catch {
		return `${profile?.symbol ?? "$"}${value.toFixed(2)}`;
	}
}
